import { useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { useAvailabilityGuard } from '@/hooks/useAvailabilityGuard'
import { FORMS, submitAuthForm, AuthSubmissionError } from '@/lib/authForm'
import { navigateTo } from '@/lib/navigate'
import FlipButton from '@/components/ui/FlipButton'

/*
  Pre-checkout landing — Returning vs New customer choice.

  Lives at /checkout/start (cart's "Proceed to Checkout" button routes
  here). Anonymous visitors only — the Liquid wrapper redirects
  authenticated visitors directly to /checkout.

  Returning customer column: email + password fields submit the
  platformOS sign_in form via submitAuthForm; on success the addon's
  sign_in_callback merges the guest cart and the form's redirect_to
  takes the user to /checkout.

  New customer column: a single "Proceed to checkout" link to
  /checkout (the actual contact step of the flow).

  Styling lives in src/styles/pages/checkout-start.css.
*/

interface Payload {
  guest_temp_uuid: string | null
}

interface SignInForm {
  email: string
  password: string
}

export default function CheckoutStart() {
  // Race-safe sold-item eviction. /checkout is the entry to the
  // intermediate flow so this is the first guard the buyer hits.
  useAvailabilityGuard()
  const payload = usePageData<Payload>() ?? { guest_temp_uuid: null }

  const [signInState, setSignInState] = useState<SignInForm>({ email: '', password: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function onChange<K extends keyof SignInForm>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setSignInState((prev) => ({ ...prev, [key]: e.target.value }))
  }

  async function handleSignIn(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!signInState.email.trim() || !signInState.password) {
      setError('Please enter your email and password.')
      return
    }
    if (!FORMS.signIn) {
      setError('Sign-in is not available right now. Please refresh the page.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const { redirectTo } = await submitAuthForm('signIn', {
        'form[email]': signInState.email.trim(),
        'form[password]': signInState.password,
        'form[redirect]': '/checkout',
        'form[guest_temp_uuid]': payload.guest_temp_uuid ?? '',
      })
      window.location.href = redirectTo || '/checkout'
    } catch (err) {
      const msg =
        err instanceof AuthSubmissionError
          ? err.message === 'Submission failed.'
            ? 'Invalid email or password.'
            : err.message
          : 'Sign-in failed. Please try again.'
      setError(msg)
      setSubmitting(false)
    }
  }

  return (
    <section className="mig-checkout-start">
      <div className="mig-checkout-landing-grid">
        {/* Returning customer */}
        <section className="mig-checkout-landing-col">
          <h2 className="mig-checkout-landing-heading">Returning customer</h2>
          <p className="mig-checkout-landing-subtitle">Sign in to pre-fill your details and check out faster.</p>

          <form onSubmit={handleSignIn} noValidate className="mig-form mig-checkout-landing-form">
            <div>
              <p className="mig-checkout-landing-form-label">
                Email <span aria-hidden="true">*</span>
              </p>
              <div className="mig-form-field">
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={signInState.email}
                  onChange={onChange('email')}
                  disabled={submitting}
                  placeholder="Enter email address"
                />
              </div>
            </div>

            <div>
              <p className="mig-checkout-landing-form-label">
                Password <span aria-hidden="true">*</span>
              </p>
              <div className="mig-form-field">
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={signInState.password}
                  onChange={onChange('password')}
                  disabled={submitting}
                  placeholder="Enter password"
                />
              </div>
            </div>

            <div className="mig-checkout-landing-forgot-row">
              <a href="/forgot-password" className="mig-checkout-landing-forgot">
                Forgot your password?
              </a>
            </div>

            {error && (
              <p role="alert" className="mig-checkout-landing-error">{error}</p>
            )}

            <FlipButton
              type="submit"
              disabled={submitting}
              fullWidth
              className="mig-checkout-landing-primary"
              data-pending={submitting}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </FlipButton>
          </form>
        </section>

        {/* Vertical divider */}
        <div aria-hidden="true" className="mig-checkout-landing-divider" />

        {/* New customer */}
        <section className="mig-checkout-landing-col">
          <h2 className="mig-checkout-landing-heading">New customer</h2>
          <p className="mig-checkout-landing-subtitle">No account creation or password is required.</p>

          <FlipButton
            fullWidth
            onClick={navigateTo('/checkout')}
            className="mig-checkout-landing-primary"
            data-with-arrow="true"
          >
            <span>Proceed to checkout</span>
            <span aria-hidden="true" className="mig-checkout-landing-primary-arrow">→</span>
          </FlipButton>
        </section>
      </div>
    </section>
  )
}
